import { Component, EventEmitter, HostBinding, Input, OnInit, Output } from '@angular/core';
import { ICrateData, IReport, IReportPositionReference } from '../../../../../models/Report';
import { CrateStateEnum } from 'src/models/Crate';
import { formatGroundPositionName } from 'src/app/helpers/get-ground-positions';
import { ActivatedRoute } from '@angular/router';
import { UserRoles } from '../../../../../models/User';
import { toExcelFormat } from 'src/app/helpers/format-position';
import { InventoryTypesEnum } from 'src/app/core/services/action.service';

type CrateStatusPresentation = {
  crateState: string;
  icon: string;
  iconBackgroundColor: string;
};

@Component({
  selector: 'app-reports-table-modal-item',
  templateUrl: './reports-table-modal-item.component.html',
  styleUrls: ['./reports-table-modal-item.component.scss']
})
export class ReportsTableModalItemComponent implements OnInit {
  @Input() item: ICrateData;
  @Input() items: ICrateData[];
  @Input() report: IReport;
  @Output() goToPalletDetails = new EventEmitter();
  @HostBinding('class.isOdd') @Input() isOdd;
  foundPosition = '---';
  expectedPosition = '';
  crateState = '';
  icon = '';
  iconBackgroundColor = '';
  expectedLevel = '---';
  foundLevel = '---';
  isMissingPallets: boolean;
  hasPalletDetails = false;
  isFoundOutsidePigeonhole = false;

  constructor(private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.item.removePallet = false;
    this.isMissingPallets = this.items.some(item => item.state === 'MISSING' || item?.inconsistency?.state === 'MISSING');
    this.hasPalletDetails = !!this.item?.id;
    this.isFoundOutsidePigeonhole = this.hasFoundOutsidePigeonhole();
    this.expectedPosition = this.formatPosition(this.item?.expectedPosition || this.item);
    this.foundPosition = this.setFoundPosition(this.item);
    this.setStatusPresentation();
    this.setLevelValues();
  }

  setFoundPosition(data: ICrateData) {
    if (this.isFoundMisplaced()) {
      return this.formatPosition(data?.foundPosition || data.inconsistency);
    }
    if (this.isMissing()) {
      return '---';
    }
    return this.formatPosition(data?.foundPosition || data);
  }

  onCheckboxChange(pallet: ICrateData) {
    pallet.removePallet = !pallet.removePallet;
  }

  canRemovePallet() {
    const userRole = this.route.firstChild.snapshot.data.role;
    return [UserRoles.ADMIN, UserRoles.MASTER].includes(userRole);
  }
  
  onPalletDetailsClick(pallet: ICrateData) {
    this.goToPalletDetails.emit(pallet);
  }

  getLevelValue(levelName?: string | null, z?: number) {
    if (levelName) return levelName;
    if (z != null) return z + 'cm';
    return '---';
  }

  isPigeonholePosition(position: IReportPositionReference | ICrateData) {
    return position?.row != null && position?.column != null;
  }

  isPigeonholeInventory() {
    return this.report?.data?.type === InventoryTypesEnum.INVENTORY_BY_PIGEONHOLE_POSITION;
  }

  hasFoundOutsidePigeonhole() {
    const foundPosition = this.item?.foundPosition;
    if (!this.isPigeonholeInventory() || !foundPosition) return false;
    return !this.isPigeonholePosition(foundPosition);
  }

  formatPigeonholePosition(position: IReportPositionReference) {
    if (!position) return '---';
    if (position?.pigeonholeAlias) return position.pigeonholeAlias;
    return `--- (coordenada: ${toExcelFormat(position.column, position.row)})`;
  }

  formatPosition(position: IReportPositionReference | ICrateData) {
    if (!position) return '---';
    if (this.isPigeonholePosition(position)) {
      return this.formatPigeonholePosition(position);
    }
    return formatGroundPositionName(position);
  }

  private setStatusPresentation() {
    const presentation = this.getStatusPresentation(this.resolveItemStatus());
    this.crateState = presentation.crateState;
    this.icon = presentation.icon;
    this.iconBackgroundColor = presentation.iconBackgroundColor;
  }

  private setLevelValues() {
    const isExpectedPigeonhole = this.isPigeonholePosition(this.item?.expectedPosition);
    const isFoundPigeonhole = this.isPigeonholePosition(this.item?.foundPosition);

    this.expectedLevel = isExpectedPigeonhole
      ? '---'
      : this.getLevelValue(this.item?.groundPositionGroupLevelName, this.item?.z);

    if (this.isMissing()) {
      this.foundLevel = '---';
      return;
    }

    if (this.isFoundMisplaced()) {
      this.foundLevel = isFoundPigeonhole ? '---' : this.getLevelValue(null, this.item?.inconsistency?.z);
      return;
    }

    this.foundLevel = isFoundPigeonhole
      ? '---'
      : this.getLevelValue(this.item?.groundPositionGroupLevelName, this.item?.z);
  }

  private resolveItemStatus(): string {
    const possibleKeyNames = ['found', 'foundMisplaced', 'missing'];
    const matchedState = possibleKeyNames.find((keyName) => keyName in this.item);

    return matchedState || this.item?.state || '';
  }

  private getStatusPresentation(status: string): CrateStatusPresentation {
    const normalizedStatus = this.normalizeStatus(status);

    switch (normalizedStatus) {
      case CrateStateEnum.FOUND_MISPLACED:
        return {
          crateState: 'Encontrado em lugar indevido',
          icon: 'times',
          iconBackgroundColor: 'warning'
        };
      case CrateStateEnum.MISSING:
        return {
          crateState: 'Perdido',
          icon: 'times',
          iconBackgroundColor: 'alert'
        };
      case CrateStateEnum.FOUND:
      default:
        return {
          crateState: 'Encontrado',
          icon: 'check',
          iconBackgroundColor: 'success'
        };
    }
  }

  private normalizeStatus(status: string): string {
    if (status?.toLowerCase().includes('misplaced')) {
      return CrateStateEnum.FOUND_MISPLACED;
    }

    return status?.toUpperCase() || CrateStateEnum.FOUND;
  }

  private isFoundMisplaced(): boolean {
    return this.item?.inconsistency?.state === CrateStateEnum.FOUND_MISPLACED;
  }

  private isMissing(): boolean {
    return this.item?.inconsistency?.state === CrateStateEnum.MISSING;
  }

}
